import { ModalLong } from '@components/base/overlays/Modal/ModalLong';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { CalendarV2 } from '@components/v2Calendar/Calendar';
import { CalendarIcon } from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';
import { Button } from '@skipper/ui';
import dayjs from 'dayjs';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';

export const CalendarPicker = React.forwardRef(
    (
        {
            type,
            initialDate,
            showTime,
            onAddTime,
            onRemoveTime,
            onDayChange,
            autoClose,
            holidays,
            showErrorOutline = false,
            maxEndDate,
            useRound = false,
            useSmall = false,
            selectTeal = false,
            placeholder,
            smallHeight,
            enableRemove,
        }: {
            type: 'assignDate' | 'dueDate' | 'date';
            initialDate?: string | dayjs.Dayjs;
            showTime?: boolean;
            onAddTime?: () => void;
            onRemoveTime?: () => void;
            onDayChange?: (day: dayjs.Dayjs) => void;
            autoClose?: boolean;
            holidays?: { [key: string]: boolean };
            showErrorOutline?: boolean;
            maxEndDate?: dayjs.Dayjs;
            useRond?: boolean;
            useSmall?: boolean;
            selectTeal?: boolean;
            placeholder?: string;
            smallHeight?: boolean;
            enableRemove?: boolean;
        },
        ref: React.ForwardedRef<any>,
    ) => {
        const { toggle } = useUniversalModalDispatch();

        return (
            <CalendarV2
                autoClose={autoClose}
                holidays={holidays}
                initialDate={initialDate}
                onDayChange={onDayChange}
                showTime={showTime}
                onAddTime={onAddTime}
                onRemoveTime={onRemoveTime}
                maxEndDate={maxEndDate}
                useRound={useRound}
                useSmall={useSmall}
                selectTeal={selectTeal}
                placeholder={placeholder}
                predicate={(newDate, setOpen) =>
                    new Promise((resolve) => {
                        if (type === 'assignDate') return resolve(true);
                        if (type === 'date') return resolve(true);
                        if (dayjs().startOf('day').isAfter(dayjs(newDate), 'day')) {
                            setOpen(false);
                            toggle(
                                true,
                                <PastDateModal
                                    onConfirm={() => {
                                        toggle(false);
                                        resolve(true);
                                    }}
                                    onCancel={() => resolve(false)}
                                />,
                            );
                            return;
                        }
                        resolve(true);
                    })
                }>
                <ButtonTarget
                    initialDate={initialDate}
                    smallHeight={smallHeight}
                    enableRemove={enableRemove}
                    onDayChange={onDayChange}
                    fmtString={
                        showTime
                            ? 'ddd, MMM DD h:mm a'
                            : type === 'date'
                            ? 'MMMM DD, YYYY'
                            : 'ddd, MMM DD'
                    }
                    placeholder={placeholder ? placeholder : 'Select a date'}
                    size={type === 'date' ? 'py-3' : ''}
                    errorBorder={showErrorOutline ? 'border-red-500' : ''}
                />
            </CalendarV2>
        );
    },
);

CalendarPicker.displayName = 'CalendarPickerComponent';

function ButtonTarget({
    initialDate,
    fmtString,
    placeholder,
    size = '',
    errorBorder,
    smallHeight,
    enableRemove,
    onDayChange,
}: {
    initialDate?: string;
    fmtString: string;
    placeholder: string;
    size?: string;
    errorBorder?: string;
    smallHeight?: boolean;
    enableRemove?: boolean;
    onDayChange?: (day: dayjs.Dayjs) => void;
}) {
    return (
        <div
            className={`flex items-center justify-between w-full shadow-sm focus:ring-1 focus:ring-primary focus:border-primary rounded-md placeholder-gray-300 form-input disabled:bg-gray-50 border-gray-300 min-w-max ${size} ${errorBorder}`}
            style={{
                minHeight: smallHeight ? '20px' : '40px',
                maxHeight: smallHeight ? '40px' : 'unset',
            }}>
            <div className="flex gap-x-2 items-center">
                <div className="text-passport">
                    {(!!initialDate &&
                        dayjs(initialDate)?.isValid() &&
                        dayjs(initialDate)?.format?.(fmtString)) || (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2">
                {enableRemove && !!initialDate && (
                        <FontAwesomeIcon
                            icon={faXmark}
                            size="sm"
                            className="cursor-pointer text-gray-500"
                            onClick={() => onDayChange?.(null)}
                        />
                    )}
                <CalendarIcon className="w-5 h-5" />
            </div>
        </div>
    );
}

export function PastDateModal({
    onConfirm,
    onCancel,
}: {
    onConfirm: () => void;
    onCancel?: () => void;
}) {
    const { toggle } = useUniversalModalDispatch();
    return (
        <ModalLong>
            <ModalLong.Header>
                <div className="flex justify-between">
                    <div className="text-passport text-base font-bold">Set a past date?</div>
                    <button
                        type="button"
                        onClick={() => {
                            onCancel?.();
                            toggle(false);
                        }}
                        className="border-none focus:outline-none focus:ring-0">
                        <XIcon className="w-4 h-4 text-gray-800" />
                    </button>
                </div>
            </ModalLong.Header>
            <ModalLong.Body>
                <div className="text-gray-800">This activity will be marked as late or missed</div>
            </ModalLong.Body>
            <ModalLong.Footer>
                <div className="w-full flex justify-end items-center gap-x-3">
                    <Button type="button" onClick={onConfirm} size="small">
                        Set date
                    </Button>
                    <Button
                        type="button"
                        size="small"
                        onClick={() => {
                            onCancel?.();
                            toggle(false);
                        }}
                        variant="white">
                        Cancel
                    </Button>
                </div>
            </ModalLong.Footer>
        </ModalLong>
    );
}
